import { supabase } from '@/integrations/supabase/client'
import {
	subDays,
	subMonths,
	subYears,
	format,
	parseISO,
	startOfMonth,
	endOfMonth,
	addMonths,
	addDays,
} from 'date-fns'

// Helper function to get start date based on time period
const getStartDate = (timePeriod: string) => {
	const now = new Date()
	switch (timePeriod) {
		case 'week':
			return subDays(now, 7)
		case 'month':
			return subMonths(now, 1)
		case 'year':
			return subYears(now, 1)
		default:
			return subDays(now, 7)
	}
}

export const getWidgetStats = async (
	widgetId: string,
	teamId: string,
	timePeriod: string = 'week'
) => {
	try {
		const startDate = getStartDate(timePeriod)

		const [widget, responses, bugs, features, reviews] = await Promise.all([
			supabase
				.from('widgets')
				.select(
					'type, enable_survey, enable_bug_reporting, enable_feature_requests, enable_reviews'
				)
				.eq('id', widgetId)
				.single(),
			supabase
				.from('responses')
				.select('score', { count: 'exact' })
				.eq('widget_id', widgetId)
				.eq('team_id', teamId)
				.gte('created_at', startDate.toISOString()),
			supabase
				.from('bug_reports')
				.select('id', { count: 'exact', head: true })
				.eq('widget_id', widgetId)
				.eq('team_id', teamId)
				.gte('created_at', startDate.toISOString()),
			supabase
				.from('feature_requests')
				.select('id', { count: 'exact', head: true })
				.eq('widget_id', widgetId)
				.eq('team_id', teamId)
				.gte('created_at', startDate.toISOString()),
			supabase
				.from('reviews')
				.select('id', { count: 'exact', head: true })
				.eq('widget_id', widgetId)
				.eq('team_id', teamId)
				.gte('created_at', startDate.toISOString()),
		])

		if (widget.error) throw widget.error
		if (!widget.data) throw new Error('Widget not found')

		let npsScore
		let avgRating

		if (widget.data.enable_survey && responses.count && responses.count > 0) {
			if (widget.data.type === 'nps') {
				const promoters = responses.data.filter((r) => r.score >= 9).length
				const detractors = responses.data.filter((r) => r.score <= 6).length
				npsScore = Math.round(
					(promoters / responses.count) * 100 -
						(detractors / responses.count) * 100
				)
			} else if (widget.data.type === 'csat') {
				const validScores = responses.data.filter(
					(r) => r.score >= 1 && r.score <= 5
				)
				if (validScores.length > 0) {
					const totalScore = validScores.reduce((acc, r) => acc + r.score, 0)
					avgRating = parseFloat((totalScore / validScores.length).toFixed(1))
				}
			}
		}

		return {
			totalResponses: responses.count || 0,
			npsScore,
			avgRating,
			bugsReported: widget.data.enable_bug_reporting ? bugs.count || 0 : 0,
			featuresSuggested: widget.data.enable_feature_requests
				? features.count || 0
				: 0,
			reviewsSubmitted: widget.data.enable_reviews ? reviews.count || 0 : 0,
		}
	} catch (error) {
		console.error('Error fetching widget stats:', error)
		throw error
	}
}

export const getResponseTrend = async (
	widgetId: string,
	teamId: string,
	timePeriod: string = 'week'
) => {
	try {
		const startDate = getStartDate(timePeriod)
		const now = new Date()

		const { data, error } = await supabase
			.from('responses')
			.select('created_at')
			.eq('widget_id', widgetId)
			.eq('team_id', teamId)
			.gte('created_at', startDate.toISOString())
			.lte('created_at', now.toISOString())

		if (error) throw error

		// Format based on time period
		const dateFormat =
			timePeriod === 'year'
				? 'MMM yyyy'
				: timePeriod === 'month'
				? 'MMM dd'
				: 'EEE'
		const groupingFormat = timePeriod === 'year' ? 'yyyy-MM' : 'yyyy-MM-dd'

		// Group responses by date
		const grouped = data.reduce((acc, curr) => {
			const date = parseISO(curr.created_at)
			const key = format(date, groupingFormat)
			acc[key] = (acc[key] || 0) + 1
			return acc
		}, {} as Record<string, number>)

		// Generate all dates in the interval
		const dates = []
		let current = startDate
		while (current <= now) {
			dates.push(current)
			current =
				timePeriod === 'year'
					? startOfMonth(addMonths(current, 1))
					: addDays(current, 1)
		}

		// Create final trend data
		return dates.map((date) => ({
			date: format(date, dateFormat),
			responses: grouped[format(date, groupingFormat)] || 0,
		}))
	} catch (error) {
		console.error('Error fetching response trend:', error)
		throw error
	}
}

export const getNPSDistribution = async (
	widgetId: string,
	teamId: string,
	timePeriod: string = 'week'
) => {
	try {
		const startDate = getStartDate(timePeriod)

		const { data, error } = await supabase
			.from('responses')
			.select('score')
			.eq('widget_id', widgetId)
			.eq('team_id', teamId)
			.gte('created_at', startDate.toISOString())
			.not('score', 'is', null)

		if (error) throw error

		const promoters = data.filter((r) => r.score >= 9).length
		const passives = data.filter((r) => r.score >= 7 && r.score <= 8).length
		const detractors = data.filter((r) => r.score <= 6).length
		const total = data.length

		const npsScore =
			total > 0
				? Math.round((promoters / total) * 100 - (detractors / total) * 100)
				: 0

		return {
			promoters,
			passives,
			detractors,
			total,
			npsScore,
			data: [
				{ name: 'Promoters', value: promoters, color: '#22c55e' },
				{ name: 'Passives', value: passives, color: '#f59e0b' },
				{ name: 'Detractors', value: detractors, color: '#ef4444' },
			],
		}
	} catch (error) {
		console.error('Error fetching NPS distribution:', error)
		throw error
	}
}

export const getCSATDistribution = async (
	widgetId: string,
	teamId: string,
	timePeriod: string = 'week'
) => {
	try {
		const startDate = getStartDate(timePeriod)

		const { data, error } = await supabase
			.from('responses')
			.select('score')
			.eq('widget_id', widgetId)
			.eq('team_id', teamId)
			.gte('created_at', startDate.toISOString())
			.gte('score', 1)
			.lte('score', 5)

		if (error) throw error

		const distribution = Array.from({ length: 5 }, (_, i) => ({
			score: i + 1,
			count: data.filter((r) => r.score === i + 1).length,
		}))

		const total = data.length
		const average =
			total > 0
				? parseFloat(
						(data.reduce((acc, r) => acc + r.score, 0) / total).toFixed(1)
				  )
				: 0

		return {
			distribution,
			total,
			average,
		}
	} catch (error) {
		console.error('Error fetching CSAT distribution:', error)
		throw error
	}
}

export const getRecentActivity = async (
	widgetId: string,
	teamId: string,
	type: 'responses' | 'bugs' | 'features' | 'reviews',
	limit: number = 5
) => {
	try {
		const tableMap = {
			responses: 'responses',
			bugs: 'bug_reports',
			features: 'feature_requests',
			reviews: 'reviews',
		}

		const table = tableMap[type]
		let query = supabase
			.from(table)
			.select(`*, widgets ( id, name, type, question )`)
			.eq('widget_id', widgetId)
			.eq('team_id', teamId)
			.order('created_at', { ascending: false })
			.limit(limit)

		const { data, error } = await query

		if (error) throw error

		// Ensure the widgets object is always present, even if null from the join
		return data.map((item: any) => ({
			...item,
			widgets: item.widgets || {
				id: widgetId,
				name: 'Unknown Widget',
				type: 'unknown',
				question: '',
			},
		}))
	} catch (error) {
		console.error(`Error fetching recent ${type}:`, error)
		throw error
	}
}
